/**
 * 인증(로그인/회원가입) 요청 오류 메시지 추출 유틸
 *
 * 429(시도 제한) 응답은 서버 detail 문자열을 우선 표시하고,
 * 없으면 Retry-After 헤더로 대기 시간을 계산하며, 헤더도 없으면 기본 문구를 사용한다.
 * (Retry-After 는 CORS 상 JS 에서 읽히지 않을 수 있으므로 detail 만으로도 동작해야 함)
 */

export const RATE_LIMIT_DEFAULT_MESSAGE = '로그인 시도가 너무 많습니다. 잠시 후 다시 시도하세요.';

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    if (value !== undefined && value !== null) return value;
  }
  const lowered = name.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === lowered);
  return key ? headers[key] : undefined;
};

/** Retry-After(초) → "로그인 시도가 너무 많습니다. N분 후 다시 시도하세요." (N = 올림 분, 최소 1) */
export const buildRateLimitMessage = (retryAfter) => {
  const seconds = Number.parseInt(retryAfter, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return RATE_LIMIT_DEFAULT_MESSAGE;
  }
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `로그인 시도가 너무 많습니다. ${minutes}분 후 다시 시도하세요.`;
};

/** detail(문자열/배열/객체) → 표시용 문자열. 해석 불가하면 null */
const parseDetail = (detail) => {
  if (typeof detail === 'string') return detail || null;
  if (Array.isArray(detail)) {
    const parsed = detail
      .map((item) => item?.msg || (typeof item === 'string' ? item : null))
      .filter(Boolean)
      .join(', ');
    return parsed || null;
  }
  if (typeof detail?.message === 'string') return detail.message;
  return null;
};

/**
 * axios 오류에서 사용자에게 보여줄 메시지를 추출한다.
 * @param {Error} error - axios 오류
 * @param {string} fallback - 서버 메시지가 없을 때 사용할 문구
 */
export const getAuthErrorMessage = (error, fallback) => {
  const response = error?.response;

  if (response?.status === 429) {
    const detailMessage = typeof response.data?.detail === 'string' ? response.data.detail : null;
    if (detailMessage) return detailMessage;
    return buildRateLimitMessage(readHeader(response.headers, 'retry-after'));
  }

  return (
    parseDetail(response?.data?.detail)
    || (typeof response?.data?.message === 'string' ? response.data.message : null)
    || error?.message
    || fallback
  );
};
